import { useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { CreditCard, Users, FileText, Calendar, Shield, BarChart3, CheckCircle, Vote } from "lucide-react";
const features = [{
  title: "Collaborative Financial Management",
  description: "Track service charges, shared expenses, and budget planning with complete transparency between all freehold shareholders.",
  icon: CreditCard
}, {
  title: "Team Decision Making",
  description: "Log important decisions, vote on property matters, and maintain a clear record of all agreed actions.",
  icon: Vote
}, {
  title: "Document Management",
  description: "Centrally store leases, insurance documents, contractor quotes, and maintenance records accessible to all shareholders.",
  icon: FileText
}, {
  title: "Maintenance Planning",
  description: "Schedule ongoing property maintenance, share tasks fairly, and track completion across all shareholders.",
  icon: Calendar
}, {
  title: "Service Charge Collection",
  description: "Streamline service charge collection, track payments, and manage the shared business bank account efficiently.",
  icon: Shield
}, {
  title: "Financial Reporting",
  description: "Generate transparent financial reports and analysis for all shareholders, simplifying annual accounts.",
  icon: BarChart3
}, {
  title: "Task Sharing",
  description: "Distribute property management tasks evenly among shareholders, ensuring fair contribution from everyone.",
  icon: CheckCircle
}, {
  title: "Multi-User Access",
  description: "Secure access for all shareholders with role-based permissions and activity tracking for complete transparency.",
  icon: Users
}];
const ModernFeatures = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll(".reveal");
          elements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add("active");
            }, index * 100);
          });
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section id="features" ref={sectionRef} className="py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="reveal text-heading-lg text-foreground mb-4">
            Managing a leasehold never felt like this
          </h2>
          <div className="h-1 w-20 bg-primary rounded-full mx-auto mb-6"></div>
          <p className="reveal text-body-lg text-muted-foreground">
            Stop managing your leasehold through WhatsApp groups and spreadsheets. Get organised with a purpose-built platform.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => <Card key={index} className="reveal group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary to-saas-blue rounded-xl flex items-center justify-center text-white shadow-md">
                    <feature.icon className="h-7 w-7" />
                  </div>
                </div>
                <h3 className="text-center text-heading-sm text-foreground mb-3">
                  {feature.title}
                </h3>
                <p className="text-center text-body-sm text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </CardContent>
            </Card>)}
        </div>

      </div>
    </section>;
};
export default ModernFeatures;